/**
 * AuthContext - Authentication state management for Business Web
 *
 * Integrates with @tonnie/sdk for authentication.
 * Provides extended user profile data alongside SDK auth.
 *
 * PRODUCTION: Uses real Clerk authentication via SDK.
 */

import { useAuth as useClerkAuth } from '@clerk/clerk-react';
import type { FamilyConfig } from '@tonnie/types';
import {
  useAuth as useSdkAuth,
  useMyPortals,
  useSubscription,
  useUpdateProfile,
  useUserProfile,
  useWebPush,
} from '@tonnie/sdk';
import { createContext, ReactNode, useCallback, useContext, useMemo } from 'react';
import { useLocation } from 'wouter';

/**
 * Remove every Tonnie-prefixed key from both session and local storage.
 * Run on logout so a second identity signing in on the same browser
 * doesn't inherit the previous user's tenant/journey/notification state.
 */
function clearTonnieStorage(): void {
  const TONNIE_PREFIX = 'tonnie_';
  for (const storage of [sessionStorage, localStorage]) {
    const keysToRemove: string[] = [];
    for (let i = 0; i < storage.length; i++) {
      const key = storage.key(i);
      if (key && key.startsWith(TONNIE_PREFIX)) keysToRemove.push(key);
    }
    keysToRemove.forEach((k) => storage.removeItem(k));
  }
}

// ═══════════════════════════════════════════════════════════════════════
// TYPES — kept for backward compatibility with existing consumers
// ═══════════════════════════════════════════════════════════════════════

export interface User {
  id: string;
  email: string;
  firstName: string;
  lastName: string;
  plan: 'free' | 'pro' | 'journey' | 'b2b2c_sponsored' | 'b2g_sponsored' | 'enterprise';
  proSubscriptionActive: boolean;
  familySubscriptionActive: boolean;
  journeyInstances: JourneyInstance[];
  profile?: UserProfile;
  linkedinConnected?: boolean;
  linkedinProfile?: {
    profileId?: string;
    profileUrl?: string;
  };
  linkedinPdfUploadedAt?: string;
  role?:
    'family_master' | 'family_member' | 'talent' | 'business_admin' | 'business_user' | 'viewer';
  /**
   * The business tenant of the membership `role` comes from (`user_tenants`, via
   * `/auth/me/portals`) — what "Tonnie staff" and "Tonnie Internal" are read
   * from (`isTonnieStaff`, `isTonnieInternal`). Undefined until that loads.
   */
  tenantId?: string;
  permissions: string[];
  familyProgram?: {
    enabled: boolean;
    journeyInstanceId: string;
    members: FamilyMember[];
  };
}

/**
 * JourneyInstance — frontend mirror after the 2026-05 cornerstone
 * refactor. Replaces the legacy
 * `accessLevel/journeyUnlockMode/journeyAccessLevel/sponsorshipType`
 * trio with `fundingSource` + `lifecycleState`.
 */
import type { FundingSource, LifecycleState } from '@tonnie/foundation';
import { runtimeEnv } from '@/core/config/runtime-env';

export interface JourneyInstance {
  id: string;
  scenario: 'pre-immigration' | 'already-abroad' | 'relocation-only' | 'integration-only';
  originCountry: string | null;
  destinationCountry: string;
  scope: {
    checkup: boolean;
    teleportal: boolean;
    qultureup: boolean;
    care: boolean;
    passport: boolean;
  };
  fundingSource: FundingSource;
  lifecycleState: LifecycleState;
  createdAt: string;
  sponsorName?: string;
  paymentInfo?: {
    paymentType: 'upfront' | 'installments';
    amount: number;
    installments?: {
      total: number;
      remaining: number;
      monthlyAmount: number;
    };
    purchaseDate: string;
    expirationDate: string;
  };
  /** Inviting tenant attribution for cross-tenant invitation flows. */
  companyName?: string | null;
  companyLogo?: string | null;
}

export interface UserProfile {
  currentCountry?: string;
  targetCountry?: string;
  role?: string;
  seniority?: 'junior' | 'mid' | 'senior';
  englishLevel?: 'A1' | 'A2' | 'B1' | 'B2' | 'C1' | 'C2';
  education?: Education[];
  experience?: Experience[];
  languages?: Language[];
  /** The onboarding family answer — the one shared shape (F35). */
  familyConfig?: FamilyConfig;
  avatarUrl?: string;
}

export interface Education {
  institution: string;
  degree: string;
  field: string;
  startDate: string;
  endDate?: string;
}

export interface Experience {
  company: string;
  role: string;
  startDate: string;
  endDate?: string;
  description?: string;
}

export interface Language {
  name: string;
  level: 'A1' | 'A2' | 'B1' | 'B2' | 'C1' | 'C2' | 'native';
}

export interface FamilyMember {
  id: string;
  email: string;
  firstName: string;
  lastName: string;
  role: 'family_member';
  invitedAt: string;
  acceptedAt?: string;
  accessLevel: 'journey_web_limited' | 'citizen_only';
  readOnlyJourneyStatus?: boolean;
}

// ═══════════════════════════════════════════════════════════════════════
// CONTEXT INTERFACE
// ═══════════════════════════════════════════════════════════════════════

interface AuthContextType {
  user: User | null;
  isLoading: boolean;
  isAuthenticated: boolean;
  /** @deprecated Use isAuthenticated instead */
  isSignedIn: boolean;
  login: (email: string, password: string) => Promise<void>;
  signup: (email: string, password: string, firstName: string, lastName: string) => Promise<void>;
  logout: () => void;
  updateUser: (updates: Partial<User>) => void;
  addJourneyInstance: (instance: Omit<JourneyInstance, 'id' | 'createdAt'>) => void;
  resetPassword: (email: string) => Promise<void>;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

// ═══════════════════════════════════════════════════════════════════════
// PROVIDER — Thin wrapper over SDK auth with real data
// ═══════════════════════════════════════════════════════════════════════

export function AuthProvider({ children }: { children: ReactNode }) {
  const { signOut: clerkSignOut } = useClerkAuth();
  const [, setLocation] = useLocation();
  const { unsubscribe: unsubscribeWebPush } = useWebPush(runtimeEnv.get('VITE_VAPID_PUBLIC_KEY'));

  // SDK authentication hooks.
  //
  // useUserProfile hits `/api/users/me` (DB-backed) — returns the resolved
  // avatarUrl (presigned S3 URL for uploaded photo) and firstName/lastName
  // from the DB. This is the source of truth for display. useCurrentUser
  // would instead hit `/api/auth/me` which returns Clerk data (imageUrl,
  // firstName/lastName snapshot) — stale after in-app edits.
  const sdkAuth = useSdkAuth();
  const { data: profileData } = useUserProfile();
  const { data: subscription } = useSubscription();
  const updateProfileMutation = useUpdateProfile();

  // BL-069: the AUTHORITATIVE business role comes from `user_tenants` (DB),
  // surfaced by `/auth/me/portals`. Clerk's `publicMetadata.role` is a cache
  // that can be stale (see BL-067), so RBAC gating must not rely on it. We
  // prefer the DB role for the `business` portal and fall back to the Clerk
  // role only until the portals query resolves. The backend BusinessAdminGuard
  // is the real security boundary; this drives UI hide/disable.
  // The tenant comes from the SAME membership: Clerk's `publicMetadata.tenantId`
  // is the same stale cache, and a membership moved in the database (Tonnie
  // staff) never reaches it.
  const { data: portalsData } = useMyPortals();
  const businessMembership = useMemo(
    () => (portalsData?.portals ?? []).find((p) => p.portal === 'business'),
    [portalsData]
  );
  const businessRole = (businessMembership?.role as User['role']) || undefined;
  const businessTenantId = businessMembership?.tenantId;

  // `isLoading` exclusively reflects Clerk's auth-resolution state.
  //
  // Previously we also blocked on `isProfileLoading || subscriptionLoading`,
  // which meant a failing / slow /users/me (e.g. a user with no business
  // membership yet) could keep the whole app in a loading state and race
  // with the PublicOnlyRoute guard — letting the Login form render before
  // the redirect kicked in and triggering Clerk's "You're already signed
  // in" error on submit. Data-loading is now a concern of individual
  // consumers (dashboard, plan gating, etc.), not of auth.
  const isLoading = !sdkAuth.isLoaded;
  const isAuthenticated = sdkAuth.isSignedIn ?? false;

  // Map SDK auth user to business-web User shape.
  //
  // Source of truth priority:
  //  - firstName / lastName: profile (PATCH /users/me) wins over Clerk. Clerk
  //    is the auth provider, not the identity store; user edits via our API
  //    are NOT synced back to Clerk.
  //  - avatarUrl: profile.avatarUrl (uploaded via POST /users/me/avatar) wins
  //    over Clerk imageUrl (social OAuth default).
  const user = useMemo<User | null>(() => {
    if (!sdkAuth.isSignedIn || !sdkAuth.user) return null;

    const clerkUser = sdkAuth.user;
    const profile = (profileData ?? null) as {
      firstName?: string;
      lastName?: string;
      avatarUrl?: string;
    } | null;

    // Subscription data from billing API, not hardcoded
    const plan = subscription?.plan || 'free';
    const proSubscriptionActive = subscription?.proSubscriptionActive ?? false;
    const familySubscriptionActive = subscription?.familyAddonActive ?? false;

    return {
      id: clerkUser.id,
      email: clerkUser.email,
      firstName: profile?.firstName || clerkUser.firstName || '',
      lastName: profile?.lastName || clerkUser.lastName || '',
      plan,
      proSubscriptionActive,
      familySubscriptionActive,
      journeyInstances: [],
      profile: {
        avatarUrl: profile?.avatarUrl || clerkUser.imageUrl,
      },
      // DB role (business portal) wins; Clerk role is the pre-load fallback.
      role: businessRole || (clerkUser.role as User['role']) || 'business_user',
      tenantId: businessTenantId,
      permissions: [],
    };
  }, [sdkAuth.isSignedIn, sdkAuth.user, subscription, profileData, businessRole, businessTenantId]);

  // Auth actions — Clerk handles these via hosted UI
  // Login/Signup pages use Clerk hooks directly; these are kept
  // as redirects for backward compatibility.
  const login = useCallback(async (_email: string, _password: string) => {
    window.location.href = '/login';
  }, []);

  const signup = useCallback(
    async (_email: string, _password: string, _firstName: string, _lastName: string) => {
      window.location.href = '/signup';
    },
    []
  );

  const logout = useCallback(async () => {
    // Clear all Tonnie-scoped session/local storage. Clerk's own storage
    // is handled by signOut(). We clear everything prefixed `tonnie_` so
    // stale tenant/journey data doesn't leak into the next identity's
    // session on the same browser.
    try {
      clearTonnieStorage();
    } catch {
      // Never let storage errors block logout
    }
    // D10: unregister this browser's push subscription WHILE still
    // authenticated — the server-side unregister call needs the session
    // that's about to go away. `unsubscribeWebPush` already swallows its own
    // errors internally, but the try/catch here holds the contract at THIS
    // call site too: sign-out must complete no matter what the SDK does.
    try {
      await unsubscribeWebPush();
    } catch {
      // A push failure should never block sign-out.
    }
    await clerkSignOut();
    setLocation('/login');
  }, [clerkSignOut, setLocation, unsubscribeWebPush]);

  const updateUser = useCallback(
    (updates: Partial<User>) => {
      const sdkUpdates = {
        firstName: updates.firstName,
        lastName: updates.lastName,
      };
      updateProfileMutation.mutate(sdkUpdates);
    },
    [updateProfileMutation]
  );

  const addJourneyInstance = useCallback((_instance: Omit<JourneyInstance, 'id' | 'createdAt'>) => {
    // Business-web does not create journey instances directly;
    // sponsorship creates them via the business/sponsor endpoint.
  }, []);

  const resetPassword = useCallback(async (_email: string) => {
    window.location.href = '/forgot-password';
  }, []);

  const contextValue = useMemo<AuthContextType>(
    () => ({
      user,
      isLoading,
      isAuthenticated,
      isSignedIn: isAuthenticated,
      login,
      signup,
      logout,
      updateUser,
      addJourneyInstance,
      resetPassword,
    }),
    [
      user,
      isLoading,
      isAuthenticated,
      login,
      signup,
      logout,
      updateUser,
      addJourneyInstance,
      resetPassword,
    ]
  );

  return <AuthContext.Provider value={contextValue}>{children}</AuthContext.Provider>;
}

// ═══════════════════════════════════════════════════════════════════════
// HOOKS
// ═══════════════════════════════════════════════════════════════════════

export function useAuth() {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
}
